import { useCallback } from "react";
import { useMutation } from "@apollo/client";

import { useToast } from "../../common/hooks/useToast";
import { getRateLimitRejection } from "../../common/rateLimit";
import { SET_USER_BANNED } from "./userBans.mutation";
import {
  BanTarget,
  UserBanStatus,
  describeBanFailure,
  normalizeReason,
  summarizeBan,
} from "./userBan";

/**
 * The one place a ban is written, shared by the ban screen and the Users list.
 *
 * Both entry points need identical wording for the outcome — in particular for
 * the anonymized cases, where the server's `warning` is the only accurate
 * description of what just happened — so the mutation, the toast and the error
 * translation live together rather than being repeated per screen.
 *
 * Returns the new state so the caller can refresh or patch its own row, and
 * `null` when nothing was written.
 */
export function useSetUserBanned() {
  const [mutate, { loading }] = useMutation(SET_USER_BANNED);
  const toast = useToast();

  const setBanned = useCallback(
    async (
      target: BanTarget,
      banned: boolean,
      reason?: string
    ): Promise<UserBanStatus | null> => {
      /*
       * `banned` is the state to set rather than a flip, so a retry after a
       * timeout cannot land the account in the opposite state.
       *
       * `reason` is only sent when banning and only when there is one: an
       * un-ban clears the field anyway, and an absent key keeps a blank textarea
       * from being written as an empty reason.
       */
      const variables: Record<string, unknown> = {
        userId: target.userId,
        banned,
      };

      const trimmed = banned ? normalizeReason(reason) : undefined;
      if (trimmed) variables.reason = trimmed;

      try {
        const result = await mutate({ variables });

        const status: UserBanStatus | undefined = result.data?.setUserBanned;

        if (!status) {
          toast.error(
            `The server did not confirm the change to ${target.username}.`
          );
          return null;
        }

        // A warning means the outcome was not the plain one — an account that was
        // already deleted by the member. Warn rather than congratulate.
        if (status.warning) {
          toast.warn(summarizeBan(status, banned));
        } else {
          toast.success(summarizeBan(status, banned));
        }

        return status;
      } catch (error) {
        // A rate-limit rejection is already announced by the global Apollo error
        // link in the same words; toasting it here would double it.
        if (!getRateLimitRejection(error)) {
          toast.error(describeBanFailure(error, target.username, banned));
        }

        return null;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mutate]
  );

  return { setBanned, saving: loading };
}
